import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  useTheme,
  IconButton,
  Button,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import check from "../assets/images/check.svg";
import cancel from "../assets/images/cancel.svg";
import rejectIcon from "../assets/images/rejectIcon.svg";
import checkIcon from "../assets/images/checkIcon.svg";
import { useState } from "react";
import { useClaimTypeStore } from "../store/useClaimTypeStore";
import { useUserStore } from "../store/useUserStore";

type SubmitPopup = {
  open: boolean;
  onClose: () => void;
  onSubmit: any;
  getGratiaFile: (value: any) => void;
  fileName: any;
  handleGratiaCheck: ((value: boolean) => void | undefined) | undefined;
  isGratiaApproved: boolean;
  claimBenefitInfo: any;
  isClaimSubmissionWindowExpired?: boolean;
  deceasedResponseDto?: any;
};

const isMainPlanBenefit = (benefit: any) => {
  const productId = benefit?.product_id ?? benefit?.productId;
  const supplementaryId =
    benefit?.supplementary_id ?? benefit?.supplementaryId;
  const supplementaryName =
    benefit?.supplementary_name ?? benefit?.supplementaryName;

  // Product rows are the main plan. The fallback supports older claim data
  // where product_id was not populated but the row was still not a top-up.
  return Boolean(productId) || (!supplementaryId && !supplementaryName);
};

const isBenefitUnderWaitingPeriod = (benefit: any) =>
  benefit?.is_under_waiting_period === true ||
  benefit?.isUnderWaitingPeriod === true;

const SubmitPopup = (props: SubmitPopup) => {
  const {
    open,
    onClose,
    onSubmit,
    getGratiaFile,
    fileName,
    isGratiaApproved,
    handleGratiaCheck,
    claimBenefitInfo,
    isClaimSubmissionWindowExpired = false,
    deceasedResponseDto,
  } = props;

  const claimType = useClaimTypeStore((state) => state.claimType);
  const isDeathClaim = claimType === "DEATH";
  const [gratiaFile, setGratiaFile] = useState<File | null>(null);

  const theme = useTheme();

  const handleSubmit = (label: string) => {
    onSubmit(label);
  };

  const benefits = Array.isArray(claimBenefitInfo) ? claimBenefitInfo : [];
  const mainPlanBenefits = benefits.filter(isMainPlanBenefit);
  const topUpBenefits = benefits.filter((item: any) => !isMainPlanBenefit(item));

  // A waiting-period top-up must not block approval of an otherwise eligible
  // main plan. Ex-Gratia is required only when the main plan itself is waiting.
  const isMainPlanUnderWaitingPeriod = mainPlanBenefits.some(
    isBenefitUnderWaitingPeriod,
  );
  const isTopUpUnderWaitingPeriod = topUpBenefits.some(
    isBenefitUnderWaitingPeriod,
  );
  const hasWaitingPeriodBenefit =
    isMainPlanUnderWaitingPeriod || isTopUpUnderWaitingPeriod;

  const role = useUserStore((state) => state.role);

  const isStillbornInvalidGestation = deceasedResponseDto?.is_still_born === true && (deceasedResponseDto?.gestation_weeks || 0) < 26;
  const isManagerStillbornBlock = role === "claim_manager" && isStillbornInvalidGestation;

  const isApproveDisabled =
    (isMainPlanUnderWaitingPeriod && !isGratiaApproved) ||
    (hasWaitingPeriodBenefit && isGratiaApproved && !gratiaFile) ||
    isClaimSubmissionWindowExpired ||
    isManagerStillbornBlock;
	
  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      aria-labelledby="otp-dialog-title"
      aria-describedby="otp-dialog-description"
      slotProps={{
        paper: {
          sx: {
            borderRadius: 5,
            overflow: "hidden",
            maxHeight: "calc(100vh - 32px)",
          },
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: "primary.main",
          height: 50,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 6,
            right: 14,
            color: "white",
            p: 1,
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          backgroundColor: theme.palette.background.default,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          px: 4,
          py: 2.5,
        }}
      >
        {isMainPlanUnderWaitingPeriod && (
          <Typography color="warning.main" fontSize={13} fontWeight={700} textAlign="center" mb={1.5}>
            Main plan is under waiting period. Select Ex-Gratia and upload the approval document to approve it.
          </Typography>
        )}
        {!isMainPlanUnderWaitingPeriod && isTopUpUnderWaitingPeriod && (
          <Typography color="warning.main" fontSize={13} fontWeight={700} textAlign="center" mb={1.5}>
            A Top-Up is under waiting period. Approve the eligible Main Plan only, or use Ex-Gratia below to include the Top-Up.
          </Typography>
        )}
        {isClaimSubmissionWindowExpired && (
          <Typography
            color="error"
            fontSize={16}
            fontWeight={600}
            textAlign="center"
          >
            Claim cannot be approved because it was submitted after the
            allowed 180-day window.
          </Typography>
        )}
        {/* Top Row: Approve and Decline side-by-side */}
        <Box
          display="flex"
          flexDirection="row"
          // sx={{flexDirection: { xs: "column", md: "row" }}}
          justifyContent="space-around"
          width="100%"
          gap={2.5}
        >
          <Box
            sx={{ flex: 1, minWidth: 0 }}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            gap={0.75}
          >
            <img src={check} alt="check" width={58} height={58} />
            <Typography fontSize={24} fontWeight={700} textAlign="center">
              Approve
            </Typography>
            <Typography
              fontSize={13}
              color="text.secondary"
              mb={1.5}
              textAlign="center"
            >
              Do you want to approve this claim?
            </Typography>
            {isManagerStillbornBlock && (
              <Typography color="error" fontSize={11} fontWeight={600} textAlign="center" mb={1.5}>
                ⚠️ Blocked: Pregnancy weeks ({deceasedResponseDto?.gestation_weeks}) &lt; 26.
              </Typography>
            )}
            <Button
              variant="contained"
              disabled={isApproveDisabled}
              onClick={() => handleSubmit("Reviewed")}
              sx={{
                backgroundColor: isApproveDisabled ? "#e0e0e0" : "#20BF55",
                color: isApproveDisabled ? "#9e9e9e" : "#fff",
                height: 42,
                width: { xs: 132, sm: 156 },
                textTransform: "none",
                fontSize: { xs: "16px", sm: "18px" },
                justifyContent: "center",
                gap: 1,
                borderRadius: 999,
                "&:hover": {
                  backgroundColor: isApproveDisabled ? "#e0e0e0" : "#20BF55",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#e0e0e0",
                  color: "#9e9e9e",
                }
              }}
            >
              <Box display="flex" sx={{ filter: isApproveDisabled ? "grayscale(100%)" : "none" }}>
                <img src={checkIcon} width={28} height={28} />
              </Box>
              Approve
            </Button>
          </Box>
          <Box
            sx={{ flex: 1, minWidth: 0 }}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            gap={0.75}
          >
            <img src={cancel} alt="cancel" width={58} height={58} />
            <Typography fontSize={24} fontWeight={700}>
              Decline
            </Typography>
            <Typography
              fontSize={13}
              color="text.secondary"
              mb={1.5}
              textAlign="center"
            >
              Do you want to decline this claim?
            </Typography>
            <Button
              variant="contained"
              onClick={() => handleSubmit("Declined")}
              sx={{
                backgroundColor: "#E8003F",
                height: 42,
                width: { xs: 132, sm: 156 },
                textTransform: "none",
                fontSize: { xs: "16px", sm: "18px" },
                justifyContent: "center",
                gap: 1,
                borderRadius: 999,
              }}
            >
              <Box display="flex">
                <img src={rejectIcon} height={28} width={28} />
              </Box>
              Decline
            </Button>
          </Box>
        </Box>
        {isDeathClaim && hasWaitingPeriodBenefit && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            mt={2.5}
            p={1.5}
            border="1px solid"
            borderColor={isGratiaApproved ? "primary.main" : "divider"}
            borderRadius={2}
            gap={2}
            sx={{
              backgroundColor: isGratiaApproved
                ? "rgba(239, 112, 0, 0.08)"
                : "background.paper",
            }}
          >
            <Box display="flex" alignItems="center" minWidth={0}>
              <Checkbox
                checked={isGratiaApproved}
                onChange={(e) => {
                  handleGratiaCheck?.(e.target.checked);
                  if (!e.target.checked) {
                    getGratiaFile(null);
                    setGratiaFile(null); // clear local file too
                  }
                }}
                color="primary"
                sx={{ p: 0.5, mr: 1 }}
              />
              <Box minWidth={0}>
                <Typography fontSize={14} fontWeight={700}>
                  Ex-Gratia Approval
                </Typography>
                <Typography fontSize={12} color="text.secondary">
                  {isMainPlanUnderWaitingPeriod
                    ? "Required because the Main Plan is under waiting period"
                    : "Optional: approve the waiting-period Top-Up by exception"}
                </Typography>
                {gratiaFile && (
                  <Typography fontSize={12} color="text.secondary" noWrap>
                    {gratiaFile.name}
                  </Typography>
                )}
              </Box>
            </Box>
            <Button
              variant={isGratiaApproved ? "contained" : "outlined"}
              component="label"
              disabled={!isGratiaApproved}
              sx={{
  textTransform: "none",
  fontSize: "13px",
  height: 34,
  minWidth: 118,
  whiteSpace: "nowrap",
  borderRadius: 999,
  "&.Mui-disabled": {
    backgroundColor: "#E0E0E0",
    color: "#9E9E9E",
    opacity: 1,
  },
}}
            >
              Upload File
              <input
                type="file"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    getGratiaFile(file);
                    setGratiaFile(file);
                  }
                }}
              />
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SubmitPopup;
